import React from 'react';
import { Trash2 } from 'lucide-react';
import { Camp, CampDate } from '../../utils/types';
import { CampFormatUtils } from '../../utils/campFormatUtils';

interface CheckoutDatesProps {
  camp: Camp;
  selectedDates: CampDate[];
  onDateRemove: (dateId: string) => void;
  onDateAdd: (dateId: string) => void;
}

const CheckoutDates: React.FC<CheckoutDatesProps> = ({
  camp,
  selectedDates,
  onDateRemove,
  onDateAdd,
}) => {
  const availableDates = camp.camp_dates || [];
  // Use string comparison to handle type mismatch between API (number) and DOM (string)
  const selectedDateIds = new Set(selectedDates.map(d => String(d.id)));

  const formatDateRow = (date: CampDate, price: number) => {
    const isSingleDay = date.start_date === date.end_date;

    if (isSingleDay) {
      // Single day: longer format with weekday (e.g., "Mon, Jan 15, 2026")
      const formattedDate = CampFormatUtils.formatDate(date.start_date, true);
      return `${formattedDate} · $${price}`;
    } else {
      // Multi-day: shorter format without weekday (e.g., "Jan 15 - Jan 20, 2026")
      const formattedRange = CampFormatUtils.formatDateRange(date.start_date, date.end_date, false);
      return `${formattedRange} · $${price}`;
    }
  };
  
  const todayStr = new Date().toISOString().slice(0, 10);
  const unselectedDates = availableDates.filter(d => !selectedDateIds.has(String(d.id)) && d.start_date >= todayStr);

  return (
    <div className="checkout-dates">
      <p className="checkout-dates-description">
        Review and confirm the dates you've chosen for this camp.
      </p>

      <div className="checkout-dates-list">
        {selectedDates.map((date) => (
          <div key={date.id} className="checkout-date-row">
            <div className="checkout-date-display">
              <span className="checkout-date-text">{formatDateRow(date, camp.price ?? 0)}</span>
            </div>
            {selectedDates.length > 1 && (
              <button
                type="button"
                className="checkout-date-remove"
                onClick={() => onDateRemove(date.id)}
                aria-label="Remove date"
              >
                <Trash2 className="icon icon--sm icon--error" />
              </button>
            )}
          </div>
        ))}
      </div>

      {unselectedDates.length > 0 && (
        <div className="checkout-add-date">
          <select
            className="form-input checkout-add-date-select"
            value=""
            onChange={(e) => {
              if (e.target.value) {
                onDateAdd(e.target.value);
              }
            }}
          >
            <option value="">+ Add another date</option>
            {unselectedDates.map(d => (
              <option key={d.id} value={d.id}>
                {formatDateRow(d, camp.price ?? 0)}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
};

export default CheckoutDates;
